import React, { Children, forwardRef, Fragment, isValidElement } from 'react'
import { cn } from '@/common/lib/cn/cn'
import { Icon } from '@/common/ui/Icon'
import { Spinner } from '@/common/ui/Spinner'
import './button.css'

/** Есть ли хоть один «видимый» потомок: не пустые строки, не пустые <>...</> */
const hasRenderableChild = (children: React.ReactNode): boolean => {
    if (children == null || typeof children === 'boolean') {
        return false
    }

    return Children.toArray(children).some(child => {
        if (child == null || typeof child === 'boolean') {
            return false
        }
        if (typeof child === 'string' || typeof child === 'number') {
            return String(child).trim() !== ''
        }
        if (isValidElement(child)) {
            if (child.type === Fragment) {
                return hasRenderableChild(child.props.children)
            }
            return true
        }
        return true
    })
}

const isChildrenEmpty = (children: React.ReactNode): boolean => !hasRenderableChild(children)

const isOnlyIconChild = (children: React.ReactNode): boolean => {
    const flat = Children.toArray(children).filter(child => {
        if (typeof child === 'string' || typeof child === 'number') {
            return String(child).trim() !== ''
        }

        return child != null
    })

    if (flat.length !== 1) {
        return false
    }

    const only = flat[0]
    if (!isValidElement(only)) {
        return false
    }

    return only.type === Icon
}

export type ButtonProps<T extends React.ElementType = 'button'> = {
    size?: 'sm' | 'md' | 'lg' | 'xl'
    emphasis?: 'accent' | 'accent-secondary' | 'neutral' | 'secondary' | 'negative' | 'positive' | 'warning' | 'promo'
    variant?: 'filled' | 'outline' | 'light' | 'ghost'
    loading?: boolean
    active?: boolean
    /** Без базовых классов `btn` — только `className` (например триггер Select с `.select`). */
    bare?: boolean
    as?: T
} & Omit<React.ComponentPropsWithoutRef<T>, 'as'>

type PolymorphicButton = <T extends React.ElementType = 'button'>(
    props: ButtonProps<T> & { ref?: React.ComponentPropsWithRef<T>['ref'] }
) => React.ReactElement | null

const ButtonRoot = forwardRef(function ButtonRoot(
    {
        children,
        size = 'md',
        emphasis = 'accent',
        variant = 'filled',
        disabled,
        loading,
        active,
        bare,
        className,
        as,
        ...props
    }: ButtonProps<React.ElementType>,
    ref: React.ForwardedRef<unknown>
) {
    const Component = as || 'button'
    const childrenEmpty = isChildrenEmpty(children)
    const iconOnly = (!bare && !loading && isOnlyIconChild(children)) || (childrenEmpty && loading)

    const buttonClasses = bare
        ? cn(className)
        : cn(
              'btn',
              'items-center justify-center',
              {
                  'btn-sm': size === 'sm',
                  'btn-md': size === 'md',
                  'btn-lg': size === 'lg',
                  'btn-xl': size === 'xl',
              },
              {
                  'btn-accent': emphasis === 'accent',
                  'btn-accent-secondary': emphasis === 'accent-secondary',
                  'btn-neutral': emphasis === 'neutral',
                  'btn-secondary': emphasis === 'secondary',
                  'btn-negative': emphasis === 'negative',
                  'btn-positive': emphasis === 'positive',
                  'btn-warning': emphasis === 'warning',
                  'btn-promo': emphasis === 'promo',
              },
              {
                  'btn-filled': variant === 'filled',
                  'btn-outline': variant === 'outline',
                  'btn-ghost': variant === 'ghost',
              },
              {
                  'btn-disabled': disabled,
                  'btn-active': active,
              },
              {
                  'btn-icon-only': iconOnly,
              },
              className
          )

    const isDisabledSupported =
        Component === 'button' || Component === 'input' || Component === 'textarea' || Component === 'select'

    return (
        <Component
            ref={ref}
            className={buttonClasses}
            {...(isDisabledSupported && disabled ? { disabled } : {})}
            {...(disabled && !isDisabledSupported
                ? {
                      'aria-disabled': true,
                      tabIndex: -1,
                      style: { pointerEvents: 'none' },
                  }
                : {})}
            {...props}
        >
            {children}
            {loading && <Spinner />}
        </Component>
    )
})

ButtonRoot.displayName = 'Button'

export const Button = ButtonRoot as PolymorphicButton
